import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, RotateCcw, Save, Wand2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

import IPOSearch from "../components/IPOSearch";
import TierList from "../components/TierList";
import DeepDivePanel from "../components/DeepDivePanel";
import CalloutCard from "../components/CalloutCard";
import AddTickerDialog from "../components/AddTickerDialog";
import { fetchIPOs, generateTierList } from "../lib/api";

const TIER_KEYS = ["S","A","B","C","D","F"];
const emptyTiers = () => ({ S: [], A: [], B: [], C: [], D: [], F: [] });

const STORAGE_PREFIX = "krishinvest_tier_v1_";
const saveTiers = (ipoId, tiers, meta) => {
  try { localStorage.setItem(STORAGE_PREFIX + ipoId, JSON.stringify({ tiers, meta })); } catch (e) { /* ignore */ }
};
const loadSaved = (ipoId) => {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + ipoId);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
};

export default function Dashboard() {
  const [ipos, setIpos] = useState([]);
  const [selectedIPO, setSelectedIPO] = useState(null);
  const [tiers, setTiers] = useState(emptyTiers());
  const [meta, setMeta] = useState({ thesis: "", why_wait_callout: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [revealKey, setRevealKey] = useState(0);

  const [panelOpen, setPanelOpen] = useState(false);
  const [panelTicker, setPanelTicker] = useState(null);

  useEffect(() => {
    fetchIPOs().then(setIpos).catch((e) => setError(e.message));
  }, []);

  const runGeneration = useCallback(async (ipo, { force = false } = {}) => {
    if (!ipo) return;
    if (!force) {
      const saved = loadSaved(ipo.id);
      if (saved && saved.tiers) {
        setTiers(saved.tiers);
        setMeta(saved.meta || {});
        setRevealKey((k) => k + 1);
        toast.success(`Loaded your saved ${ipo.name} tier list`);
        return;
      }
    }
    setLoading(true); setError(null);
    try {
      const data = await generateTierList(ipo);
      const next = emptyTiers();
      (data.tiers || []).forEach((t) => {
        if (TIER_KEYS.includes(t.tier)) next[t.tier] = t.tickers || [];
      });
      const nextMeta = { thesis: data.thesis, why_wait_callout: data.why_wait_callout || [], sector: data.sector };
      setTiers(next);
      setMeta(nextMeta);
      setRevealKey((k) => k + 1);
      saveTiers(ipo.id, next, nextMeta);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      toast.error("Failed to generate tier list");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelect = (ipo) => {
    setSelectedIPO(ipo);
    setTiers(emptyTiers());
    setMeta({ thesis: "", why_wait_callout: [] });
    runGeneration(ipo);
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const next = { ...tiers };
    const srcArr = Array.from(next[source.droppableId] || []);
    const [moved] = srcArr.splice(source.index, 1);
    const dstArr = source.droppableId === destination.droppableId
      ? srcArr
      : Array.from(next[destination.droppableId] || []);
    dstArr.splice(destination.index, 0, moved);
    next[source.droppableId] = source.droppableId === destination.droppableId ? dstArr : srcArr;
    next[destination.droppableId] = dstArr;
    setTiers(next);
  };

  const handleTickerClick = (t) => {
    setPanelTicker(t);
    setPanelOpen(true);
  };

  const handleSave = () => {
    if (!selectedIPO) return;
    saveTiers(selectedIPO.id, tiers, meta);
    toast.success("Your tier list is saved locally");
  };

  const handleReset = () => {
    if (!selectedIPO) return;
    runGeneration(selectedIPO, { force: true });
  };

  const handleAddTicker = ({ tier, chip }) => {
    if (!TIER_KEYS.includes(tier)) tier = "B";
    setTiers((prev) => {
      const next = { ...prev, [tier]: [...(prev[tier] || []), chip] };
      if (selectedIPO) saveTiers(selectedIPO.id, next, meta);
      return next;
    });
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Subtle stars in the background of the dashboard too */}
      <div className="stars-bg pointer-events-none opacity-60">
        <div className="stars-layer s1" />
        <div className="stars-layer s2" />
      </div>

      <div className="relative z-10 mx-auto max-w-6xl px-4 py-12 md:px-8" data-testid="dashboard">
        {/* Header */}
        <header className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <div className="font-mono-jb text-[11px] uppercase tracking-[0.22em] text-zinc-500">
              KRISHINVEST · IPO ALTERNATIVES
            </div>
            <h1 className="mt-2 font-chivo text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
              Skip the IPO. <span className="text-indigo-300">Play the thesis.</span>
            </h1>
            <p className="mt-3 max-w-xl text-zinc-400">
              Pick a hyped private company. We&apos;ll rank the best publicly-traded stocks that capture the same bet — today.
            </p>
          </div>
        </header>

        {/* Step 1 */}
        <section className="mt-10">
          <IPOSearch ipos={ipos} selected={selectedIPO} onSelect={handleSelect} />
        </section>

        {/* Step 2 */}
        {selectedIPO && (
          <section className="mt-10" data-testid="tier-list-section">
            <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
              <div>
                <div className="font-mono-jb text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                  Step 2 · Tier list for
                </div>
                <h2 className="mt-1 font-chivo text-2xl font-bold tracking-tight md:text-3xl">
                  {selectedIPO.name}
                  <span className="ml-2 font-mono-jb text-base text-zinc-500">${selectedIPO.ticker}</span>
                </h2>
                {meta.thesis && <p className="mt-1 max-w-2xl text-sm text-zinc-400">{meta.thesis}</p>}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <AddTickerDialog ipoName={selectedIPO.name} onAdd={handleAddTicker} />
                <Button
                  variant="outline"
                  className="border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                  onClick={handleSave}
                  data-testid="save-tier-list-button"
                >
                  <Save className="mr-2 h-4 w-4" /> Save
                </Button>
                <Button
                  variant="outline"
                  className="border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                  onClick={handleReset}
                  data-testid="reset-tier-list-button"
                >
                  <RotateCcw className="mr-2 h-4 w-4" /> Regenerate
                </Button>
              </div>
            </div>

            <div className="mt-6">
              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex min-h-[260px] flex-col items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/[0.015] py-16 text-zinc-400"
                    data-testid="tier-list-loading"
                  >
                    <div className="flex items-center gap-3">
                      <Wand2 className="h-5 w-5 animate-pulse text-indigo-300" />
                      <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                    <span className="font-mono-jb text-xs uppercase tracking-[0.2em]">
                      Generating tier list for {selectedIPO.name}…
                    </span>
                  </motion.div>
                ) : error ? (
                  <motion.div
                    key="error"
                    className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300"
                    data-testid="tier-list-error"
                  >
                    {error}
                  </motion.div>
                ) : (
                  <motion.div key={`tl-${revealKey}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <TierList
                      tiers={tiers}
                      onDragEnd={handleDragEnd}
                      onTickerClick={handleTickerClick}
                      revealKey={revealKey}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Callout */}
            {!loading && meta.why_wait_callout && meta.why_wait_callout.length > 0 && (
              <div className="mt-8">
                <CalloutCard bullets={meta.why_wait_callout} ipoName={selectedIPO.name} />
              </div>
            )}
          </section>
        )}

        {!selectedIPO && (
          <div
            className="mt-16 rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center"
            data-testid="empty-state"
          >
            <p className="font-mono-jb text-[11px] uppercase tracking-[0.22em] text-zinc-500">
              Awaiting selection
            </p>
            <p className="mt-3 font-chivo text-xl text-zinc-300">
              Pick a private company above to generate your tier list.
            </p>
          </div>
        )}

        <footer className="mt-16 border-t border-white/10 pt-6 text-center font-mono-jb text-[10px] uppercase tracking-[0.22em] text-zinc-600">
          Built for retail · Not investment advice
        </footer>
      </div>

      <DeepDivePanel
        open={panelOpen}
        onOpenChange={setPanelOpen}
        ticker={panelTicker}
        ipoName={selectedIPO?.name}
      />
    </div>
  );
}
